import get from 'lodash/get';
import isString from 'lodash/isString';
import takeWhile from 'lodash/takeWhile';

import { ContainerAccess, RoleMatrix } from './enums/container-access.enum';
import { ContainerType } from './enums/container-type.enum';
import { SiteRole } from './enums/site-role.enum';
import { Container } from './models/container.model';
import { Group } from './models/group.model';
import { Project } from './models/project.model';
import { User } from './models/user.model';
import { SearchResult } from './models/search/search-results.model';

const containerTypes: Set<string> = new Set(Object.values(ContainerType));
export function isContainerType(type: string): type is ContainerType {
  return containerTypes.has(type);
}

export function getContainerType(container: Container): ContainerType {
  if (container.container_type) {
    return container.container_type;
  }
  if (container.curator) {
    return ContainerType.Collection;
  }
  if (container.job && container.parent) {
    return ContainerType.Analysis;
  }
  if (container.session) {
    return ContainerType.Acquisition;
  }
  if (container.subject) {
    return ContainerType.Session;
  }
  if (container.project) {
    return ContainerType.Subject;
  }
  if (container.group) {
    return ContainerType.Project;
  }
  throw new Error('Cannot determine container type.');
}

export function pluralize(containerType: string): string {
  if (!isContainerType(containerType)) {
    return containerType;
  }
  if (containerType === 'analysis') {
    return 'analyses';
  }
  return containerType + 's';
}

export function isLabEdition(container: Group | Project): boolean {
  return get(container, 'editions.lab', false);
}

export function getRoleMatrix(container: Pick<Container, 'permissions'>, user: User): RoleMatrix {
  const maxAccess = SiteRole.isSiteAdmin(user)
    ? ContainerAccess.valueOf(ContainerAccess.Admin)
    : Math.max(0,
      ...(container.permissions || [])
        .filter(({ _id }) => _id === user._id)
        .map(permission => 'access' in permission ? ContainerAccess.valueOf(permission.access) : 0));
  return ContainerAccess.roleMatrix(maxAccess);
}

interface RoutingSegments {
  group?: string
  project?: string
  subject?: string
  session?: string
}

export function buildRoutingString({ group, project, subject, session }: RoutingSegments): string {
  const segments = takeWhile([group, project, subject, session], Boolean);
  return `fw://${segments.join('/')}`;
}

/**
 * Recursive helper function to flatten the subject info fields.
 * For now they are being entered and displayed using dot notation.
 * Example: { a: { b: {c: 'test' } } } becomes { 'a.b.c = 'test' }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenInfoKeys(target: Record<string, any>, prefix: string): Record<string, any> {
  const propName = prefix ? prefix + '.' : '';
  const flattenedObj = {};
  for (const attr in target) {
    if (target[attr] instanceof Array) {
      flattenedObj[attr] = target[attr].join(',');
    } else if (typeof target[attr] === 'object') {
      Object.assign(flattenedObj, flattenInfoKeys(target[attr], propName + attr));
    } else {
      flattenedObj[propName + attr] = target[attr];
    }
  }
  return flattenedObj;
}

export function getContainerProjectId(container: Container | SearchResult): string {
  if (Object.keys(container).includes('_source')) {
    const searchResult = container as SearchResult;
    return get(searchResult._source, 'project._id');
  }
  container = container as Container;
  if (container.parents) {
    return isString(container.parents.project) ? container.parents.project : container._id;
  }
  return container.project;
}
